import {
    AfterViewInit,
    ChangeDetectionStrategy,
    Component,
    EventEmitter,
    ElementRef,
    Input,
    OnDestroy,
    OnInit,
    Output,
    ViewContainerRef,
    inject,
    viewChild,
    effect
} from '@angular/core';
import {of, Subject} from 'rxjs';
import {debounceTime, distinctUntilChanged, filter, switchMap, takeUntil, take} from 'rxjs/operators';
import {CdkConfigService} from '@cdk/services/config.service';
import {Lotacao, Pagination, Usuario} from '../../models';
import {FormBuilder, FormGroup, FormsModule, ReactiveFormsModule} from '@angular/forms';
import {
    CdkChaveAcessoPluginComponent
} from '../chave-acesso/cdk-chave-acesso-plugins/cdk-chave-acesso-plugin.component';
import {LoginService} from '../../../app/main/auth/login/login.service';
import {DynamicService} from 'modules/dynamic.service';
import {modulesConfig} from 'modules/modules-config';
import {SearchBarService} from '../search-bar/search-bar.service';
import {Router} from '@angular/router';
import {NgClass} from '@angular/common';
import {CdkProcessoSearchAutocompleteComponent} from '../processo/cdk-processo-search-autocomplete/cdk-processo-search-autocomplete.component';
import {MatIconButton} from "@angular/material/button";
import {MatIcon} from "@angular/material/icon";
import {MatInput} from "@angular/material/input";
import {MatAutocompleteTrigger} from "@angular/material/autocomplete";
import {MatDialog} from "@angular/material/dialog";

@Component({
    selector: 'cdk-barra-pesquisa',
    templateUrl: './barra-pesquisa.component.html',
    styleUrls: ['./barra-pesquisa.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        FormsModule,
        ReactiveFormsModule,
        MatIconButton,
        MatIcon,
        NgClass,
        MatInput,
        MatAutocompleteTrigger,
        CdkProcessoSearchAutocompleteComponent,
    ]
})
export class BarraPesquisaComponent implements OnInit, AfterViewInit, OnDestroy {
    private _cdkConfigService = inject(CdkConfigService);
    private _formBuilder = inject(FormBuilder);
    private _loginService = inject(LoginService);
    private _dialog = inject(MatDialog);
    _searchBarService = inject(SearchBarService);
    private _dynamicService = inject(DynamicService);
    private _router = inject(Router);

    readonly buscaComponent = viewChild('buscaComponent', {read: ViewContainerRef});

    readonly hiddenComponent = viewChild('hiddenComponent', {read: ViewContainerRef});

    readonly inputLabel = viewChild<ElementRef<HTMLLabelElement>>('inputLabel');

    @Input()
    processoPagination: Pagination;

    @Output()
    inputText: EventEmitter<any>;

    collapsed: boolean;
    cdkConfig: any;

    form: FormGroup;

    activeCard = 'form';

    module = 'administrativo';

    // Private
    private _unsubscribeAll: Subject<any>;

    usuario: Usuario;

    isUserConsultivo: boolean;


    /**
     * Constructor
     *
     */
    constructor() {
        this.form = this._formBuilder.group({
            processo: [null],
            content: [null]
        });

        this.processoPagination = new Pagination();
        this.processoPagination.populate = ['especieProcesso', 'especieProcesso.generoProcesso', 'setorAtual', 'setorAtual.unidade'];
        this.processoPagination.context = {'modulo': this.module};

        // Set the defaults
        this.inputText = new EventEmitter();
        this.collapsed = true;

        // Set the private defaults
        this._unsubscribeAll = new Subject();

        this.usuario = this._loginService.getUserProfile();

        //ADAPTAÇÃO PARA PILOTO DE PESQUISA CONSULTIVA
        this.isUserConsultivo = this.usuario?.colaborador?.lotacoes.some(l =>
            l.setor?.especieSetor?.generoSetor?.nome === 'CONSULTIVO' ||
            l.setor?.unidade?.modalidadeOrgaoCentral?.valor === 'CGU' ||
            l.setor?.unidade?.generoSetor?.nome === 'CONSULTIVO'      ||
            l.setor?.id === 2230                                      || //Coordenação de Análise de Documentos e Processos - Gabinete AGU
            l.setor?.especieSetor?.generoSetor?.nome === 'CORREICIONAL' ||
            l.setor?.unidade?.generoSetor?.nome === 'CORREICIONAL'      ||
            l.setor?.unidade?.modalidadeOrgaoCentral?.valor === 'CGAU'
        );
        // const unidadesPermitidasId = [
        //     96139 //SGE
        // ];
        // const setoresPermitidosId: number[] = [
        //     2230,   //COAD
        //     1509    //CSPA
        // ];
        // this.isUserConsultivo = this.usuario?.colaborador?.lotacoes?.some((l: Lotacao) => {
        //     return unidadesPermitidasId.includes(l?.setor?.unidade?.id) || setoresPermitidosId.includes(l.setor?.id);
        // });
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    /**
     * On init
     */
    ngOnInit(): void {
        // Subscribe to config changes
        this._cdkConfigService.config.pipe(takeUntil(this._unsubscribeAll)).subscribe((config) => {
            this.cdkConfig = config;
        });

        if (this.form.get('processo')) {
            this.form.get('processo').valueChanges.pipe(
                debounceTime(300),
                distinctUntilChanged(),
                switchMap((value) => {
                    if (value && typeof value === 'object') {
                        if (value.visibilidadeExterna || this._loginService.isGranted('ROLE_COLABORADOR')) {
                            this.inputText.emit({id: value.id});
                            return of([]);
                        }

                        const dialogRef = this._dialog.open(CdkChaveAcessoPluginComponent, {
                            width: '600px'
                        });

                        dialogRef.afterClosed().pipe(filter(result => !!result), take(1)).subscribe((result) => {
                            this.inputText.emit({id: value.id, chaveAcesso: result});
                            return of([]);
                        });
                    }

                    return of([]);
                }),
                takeUntil(this._unsubscribeAll)
            ).subscribe();
        }
    }

    ngAfterViewInit(): void {
        const path = '@cdk/components/search-bar';
        const pathPesquisa = '@cdk/components/search-bar-busca';

        modulesConfig.forEach((module) => {
            if (module.components.hasOwnProperty(pathPesquisa)) {
                module.components[pathPesquisa].forEach(((c) => {
                    this._dynamicService.loadComponent(c)
                        .then((componentFactory) => {
                            if (this.isUserConsultivo) {
                                this.buscaComponent().createComponent(componentFactory);
                            }
                        });
                }));
            }
        });

        modulesConfig.forEach((module) => {
            if (module.components.hasOwnProperty(path)) {
                module.components[path].forEach(((c) => {
                    this._dynamicService.loadComponentNew(c)
                        .then((result) => {
                            this.hiddenComponent().createComponent(result);
                        });
                }));
            }
        });
    }

    /**
     * On destroy
     */
    ngOnDestroy(): void {
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next(true);
        this._unsubscribeAll.complete();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Collapse
     */
    collapse(): void {
        this.collapsed = true;
    }

    /**
     * Expand
     */
    expand(): void {
        this.collapsed = false;
    }

    checkProcesso(): void {
        const processo = this.form.get('processo').value;
        if (!processo || typeof processo !== 'object') {
            this.form.get('processo').setValue(null);
        }
    }

    routeSearch(): void {
        this._router.navigate(['/apps/pesquisa/processos']).then()
    }

    onFocus() {
        this.setFloatingLabel(true);
    }

    onBlur() {
        if (!this.form.get('processo')?.value) {
            this.setFloatingLabel(false);
        }
    }

    onInput() {
        this.setFloatingLabel(!!this.form.get('processo')?.value);
    }

    private setFloatingLabel(isFloating: boolean) {
        const label = this.inputLabel()?.nativeElement;
        if (label) {
            label.classList.toggle('floating', isFloating);
        }
    }
}
